import {
  HistoryResponse,
  ChatStreamRequest,
  ChatStreamRequestV2,
} from "../types";
// deprecated streaming utilities removed
import { authenticatedFetch } from "../utils/apiUtils"; // Import the new utility
import { getApiBaseUrl } from "./configService";
import {
  streamChat as apiStreamChat,
  streamChatWithContext as apiStreamChatWithContext,
} from "./apiClientV2";
import { AppError } from "../types/errors";

// const API_BASE_URL = '/api/v1'; // Using Vite proxy - REMOVED This was causing issues in WP context

// Base URLs are now provided by the centralized config service

/**
 * Fetches chat history for a given session ID.
 * @param sessionId The UUID of the chat session.
 * @param limit The maximum number of messages per page.
 * @param offset The number of messages to skip.
 * @returns A promise that resolves to the HistoryResponse object.
 */
export async function fetchChatHistory(
  sessionId: string,
  limit: number = 30,
  offset: number = 0,
): Promise<HistoryResponse> {
  const API_BASE_URL = getApiBaseUrl("v2");
  const url = `${API_BASE_URL}/chat/history?session_id=${encodeURIComponent(
    sessionId,
  )}&limit=${limit}&offset=${offset}`;

  return authenticatedFetch<HistoryResponse>(
    url,
    "chatService",
    "fetchChatHistory",
  );
}

/**
 * Define the new response type for session summaries (mirroring backend schema)
 * This might be better placed in a dedicated types file (e.g., types/chat.ts or types/index.ts)
 * For now, adding it here for context. If types/index.ts is preferred, we'll move it.
 */
export interface ChatSessionSummary {
  session_id: string; // UUID string
  title: string | null;
  last_activity_at: string; // ISO datetime string
  message_count: number;
}

export interface PaginatedChatSessionSummaryResponse {
  sessions: ChatSessionSummary[];
  total_sessions: number;
  limit: number;
  offset: number;
}

/**
 * Fetches paginated chat session summaries for the authenticated user.
 * @param limit The maximum number of session summaries per page.
 * @param offset The number of session summaries to skip.
 * @returns A promise that resolves to the PaginatedChatSessionSummaryResponse object.
 */
export async function fetchChatSessionSummaries(
  limit: number = 10,
  offset: number = 0,
  mindspaceId?: string,
): Promise<PaginatedChatSessionSummaryResponse> {
  const API_BASE_URL = getApiBaseUrl("v2");
  let url = `${API_BASE_URL}/chat/history/sessions?limit=${limit}&offset=${offset}`;

  if (mindspaceId) {
    url += `&mindspace_id=${encodeURIComponent(mindspaceId)}`;
  }

  return authenticatedFetch<PaginatedChatSessionSummaryResponse>(
    url,
    "chatService",
    "fetchChatSessionSummaries",
  );
}

/**
 * Initiates a streaming chat response from the backend and processes SSE events.
 * @param requestData The data including sessionId, message, and optional currentPageUrl.
 * @param onToken Callback for when a 'token' event is received.
 * @param onEnd Callback for when the stream ends.
 * @param onError Callback for when an error occurs (from stream or initial request).
 * @param onStreamControllerCreated Callback to provide the AbortController to the store for cancellation.
 */
/**
 * @deprecated Use apiClientV2.streamChat instead. This wrapper forwards to the unified v2 stream.
 */
export async function streamChatResponse(
  requestData: ChatStreamRequest,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (error: AppError) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
): Promise<void> {
  // Forward to unified v2 streaming via apiClientV2
  const maybe = requestData as Partial<
    import("../services/apiClientV2").ChatStreamUnifiedRequest
  >;
  const unifiedRequest: import("../services/apiClientV2").ChatStreamUnifiedRequest =
  {
    session_id: requestData.session_id,
    message: requestData.message,
    template_id: maybe.template_id,
    context_override: maybe.context_override,
  };
  return apiStreamChat(
    unifiedRequest,
    onToken,
    onEnd,
    onError,
    onStreamControllerCreated,
  );
}

/**
 * Initiates a streaming chat response against the v2 context-aware endpoint.
 * @param mindspaceId The ID of the mindspace associated with the chat.
 * @param requestData The request payload conforming to ChatStreamRequestV2.
 * @param onToken Callback when a token chunk is received.
 * @param onEnd Callback when the stream ends naturally.
 * @param onError Callback when an error occurs.
 * @param onStreamControllerCreated Callback that provides the AbortController for external cancellation.
 */
/**
 * @deprecated Use apiClientV2.streamChatWithContext or apiClientV2.streamChat instead.
 */
export async function streamChatResponseV2(
  mindspaceId: string,
  requestData: ChatStreamRequestV2,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (error: AppError) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
): Promise<void> {
  // Forward to v2 context-aware streaming via apiClientV2
  const maybe = requestData as Partial<
    import("../services/apiClientV2").ChatStreamUnifiedRequest
  >;
  const mappedRequest: import("../services/apiClientV2").ChatStreamRequest = {
    session_id:
      (maybe.session_id as string) ||
      (requestData as unknown as { session_id?: string }).session_id ||
      "",
    message: requestData.message,
    template_id: maybe.template_id,
    context_override: maybe.context_override,
  };
  return apiStreamChatWithContext(
    mindspaceId,
    mappedRequest,
    onToken,
    onEnd,
    onError,
    onStreamControllerCreated,
  );
}

/**
 * Initiates a streaming chat response against the unified guest-friendly v2 endpoint.
 * This endpoint works for both authenticated and guest users. When `mindspace_id` is omitted the
 * backend treats the request as guest-only and relies on the provided `session_context` instead of
 * persistent mindspace context.
 *
 * @param requestData  The request payload conforming to ChatStreamUnifiedRequest (see types.ts).
 * @param onToken      Callback when a token chunk is received.
 * @param onEnd        Callback when the stream ends naturally.
 * @param onError      Callback when an error occurs.
 * @param onStreamControllerCreated  Callback that provides the AbortController for external cancellation.
 */
/**
 * @deprecated Use apiClientV2.streamChat instead.
 */
export async function streamChatResponseUnified(
  requestData: import("../types").ChatStreamUnifiedRequest,
  onToken: (chunk: string) => void,
  onEnd: () => void,
  onError: (error: AppError) => void,
  onStreamControllerCreated: (controller: AbortController | null) => void,
): Promise<void> {
  // Forward to unified v2 streaming via apiClientV2
  return apiStreamChat(
    requestData as unknown as import("./apiClientV2").ChatStreamUnifiedRequest,
    onToken,
    onEnd,
    onError,
    onStreamControllerCreated,
  );
}
